/**
 * /multiplayer page glue (multiplayer-surface design): entering a run (persist the game handle,
 * navigate — the board's own boot owns the deal now, § Entering a run — the board deals), and the
 * unregistered nudge's inline registration. Restructured on the games-list-enhance.js precedent
 * (review finding): `wirePage(root)` does all the wiring — branching on which sections the
 * passed-in root carries — so tests can drive it over fixture DOM; the bottom-of-file self-wire
 * guard calls it for the real page.
 *
 * The run-card mount + click wiring moved to `multiplayer-tables.js#enhanceTables`
 * (multiplayer-entry-and-notifications design § Structure) — the start-screen section shares it
 * now. Since PR 2, the create-table form wiring moved the same way to
 * `multiplayer-tables.js#wireCreateForm` — the section's create-row overlay is a second mounted
 * instance now, and the extraction is what keeps their `invitees`/`gameMode` selection state from
 * cross-talking. `enterGame` below is this page's own `onEnter`/`onCreated` (persist + navigate).
 */

import { persistGameHandle } from "./game-handle.js";
import { enhanceTables, wireCreateForm } from "./multiplayer-tables.js";

const CSRF_HEADERS = { "X-Requested-With": "XMLHttpRequest" };

/**
 * Enter a run: persist the handle and navigate. Nothing else — sitting-down-deals moved to the
 * board's own boot (multiplayer-surface design § Entering a run — the board deals; the prototype's
 * client-side probe-then-deal workaround is retired, review round 1). Also this page's own
 * `onCreated` — a fresh table is entered the same way a run card is.
 */
function enterGame(gameId) {
  persistGameHandle(gameId);
  location.href = "/";
}

function wireRegisterNudge(root) {
  const form = root.querySelector("#mp-register-form");
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const errorLine = root.querySelector("#mp-register-error");
    errorLine.hidden = true;
    try {
      const response = await fetch("/api/register", {
        method: "POST",
        headers: { ...CSRF_HEADERS, "Content-Type": "application/json" },
        body: JSON.stringify({
          name: root.querySelector("#mp-register-name").value.trim(),
          password: root.querySelector("#mp-register-password").value,
        }),
      });
      if (response.ok) {
        location.reload();
        return;
      }
      const body = await response.json().catch(() => null);
      errorLine.textContent = body?.error ?? errorLine.dataset.msgFailed;
      errorLine.hidden = false;
    } catch (err) {
      console.error("[multiplayer-page] register failed:", err?.message ?? err);
      errorLine.textContent = errorLine.dataset.msgFailed;
      errorLine.hidden = false;
    }
  });
}

/**
 * Wires the whole page over `root` — the nudge form when unregistered, else the shared
 * enhance/create wiring. Exported so tests can drive it over fixture DOM (the
 * games-list-enhance.js precedent); the bottom-of-file guard calls it with `document` for the
 * real page.
 */
export function wirePage(root) {
  if (root.querySelector("#mp-register-form") != null) {
    wireRegisterNudge(root);
  } else {
    enhanceTables(root, { onEnter: enterGame });
    wireCreateForm(root, { onCreated: enterGame });
  }
}

// Guarded on the page's own root marker, not just `typeof document` (games-list-enhance.js's
// guard is enough there since its own functions all tolerate a zero-match root; this module's
// wiring functions assume specific elements exist, so a stray import in a test with an unrelated
// or empty document must not attempt to wire at all).
if (
  typeof document !== "undefined" &&
  document.querySelector(".multiplayer-page") != null
) {
  wirePage(document);
}
